import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useSidebar } from '../../context/SidebarContext';
import { NavLink, Link, useNavigate } from 'react-router-dom';
import { FaSearch, FaEnvelope, FaPhone, FaBell, FaUser, FaCog } from "react-icons/fa";
import { useLogout } from '../../hooks/useLogout';
import { toast } from 'react-toastify';
import { Navbar, Nav, Form, Button, Badge, Dropdown } from "react-bootstrap";
import { useClients } from '../../hooks/useGetClients';
import { useMyAssignedTickets } from '../../hooks/TICKET/useGetTickets';

const STATUS_LABEL = { open: 'Ouvert', in_progress: 'En cours' };
const STATUS_BG    = { open: 'primary', in_progress: 'warning' };

const Topbar = () => {
  // Auth & hooks
  const { user, isAdmin, hasPermission } = useAuth();
  const { collapsed } = useSidebar();
  const logoutMutation = useLogout();
  const navigate = useNavigate();

  // Search state
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const dropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Client search hook
  const { data, isLoading } = useClients(1, 10, debouncedSearch);

  // Normalize clients array
  const clients = useMemo(
    () => (Array.isArray(data?.data?.clients) ? data.data.clients : []),
    [data]
  );

  // Notifications — tickets assigned to current user, filtered client-side (same pattern as Home.jsx)
  const userId = user?.data?.id;
  const { data: notifications = [], isLoading: notifLoading } = useMyAssignedTickets(userId);
  const notifCount = notifications.length;

  // Responsive
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1000);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1000);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target) &&
        searchInputRef.current &&
        !searchInputRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset highlight index when results change
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [clients]);

  // Debounce: attend 500ms après la dernière frappe avant de déclencher la requête.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Logout handler
  const handleClick = (e) => {
    e.preventDefault();
    const logoutPromise = logoutMutation.mutateAsync();
    toast.promise(
      logoutPromise.then(() => {
        return navigate('/');
      }),
      {
        pending: 'Déconnexion en cours...',
        success: 'Déconnexion réussie!',
        error: 'Échec de la déconnexion',
      }
    );
  };

  // Update search & toggle dropdown
  const handleSearchChange = (e) => {
    let value = e.target.value;
    if (!isNaN(value)) {
      value = value.toString();
    }
    setSearch(value);
    setShowDropdown(value.trim().length > 0);
  };

  // Navigate to client page
  const handleClientSelect = (client) => {
    setSearch("");
    setShowDropdown(false);
    navigate(`/client/${client.id}`);
  };

  // Keyboard navigation for results
  const handleKeyDown = (e) => {
    if (!showDropdown || clients.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < clients.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : -1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && clients[highlightedIndex]) {
          handleClientSelect(clients[highlightedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSearch("");
        break;
      default:
        break;
    }
  };

  // Highlight matched query in text
  const highlightMatch = (text, query) => {
    if (!query.trim()) return text;

    const highlightColor = 'var(--bs-blue)';
    const textColor = 'var(--bs-white)'

    const parts = text.split(new RegExp(`(${query})`, 'gi'));
    return parts.map((part, index) =>
      part.toLowerCase() === query.toLowerCase() ? (
        <mark key={index} style={{ backgroundColor: highlightColor, padding: '0 2px', color: textColor }}>
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  // Mobile version
  if (isMobile) {
    return (
      <>
        <Navbar expand="lg" className="shadow mb-4 navbar-fixed-top" style={{ zIndex: 1, height: '4.375rem' }}>
          <div className="d-flex align-items-center w-100 px-3 position-relative">
            {/* Mobile search bar on the left */}
            <div className="d-flex align-items-center position-relative flex-grow-1 me-3">
              <Form.Control
                ref={searchInputRef}
                type="text"
                placeholder="Chercher un client..."
                className="form-control-user"
                style={{ 
                  height: "40px", 
                  borderTopRightRadius: 0, 
                  borderBottomRightRadius: 0,
                }}
                value={search}
                onChange={handleSearchChange}
                onKeyDown={handleKeyDown}
                onFocus={() => search.trim().length > 0 && setShowDropdown(true)}
              />
              <Button
                variant="primary"
                style={{
                  backgroundColor: "#4E73DF",
                  border: "none",
                  height: "40px",
                  width: "40px",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderTopLeftRadius: 0,
                  borderBottomLeftRadius: 0,
                }}
                onClick={() => search.trim().length > 0 && setShowDropdown(true)}
              >
                <FaSearch />
              </Button>

              {/* Clients dropdown */}
              {showDropdown && (
                <div
                  ref={dropdownRef}
                  className="position-absolute bg-body shadow rounded"
                  style={{
                    top: "calc(100% + 5px)",
                    left: 0,
                    right: 0,
                    maxHeight: "300px",
                    overflowY: "auto",
                    zIndex: 10,
                    color: "var(--bs-body-color)",
                  }}
                >
                  {isLoading ? (
                    <div className="p-3 text-center text-muted small">
                      Recherche en cours...
                    </div>
                  ) : clients.length > 0 ? (
                    <div>
                      {clients.map((client, index) => (
                        <div
                          key={client.id}
                          className="p-2 cursor-pointer"
                          style={{
                            cursor: 'pointer',
                            borderBottom: index < clients.length - 1 ? '1px solid var(--bs-border-color)' : 'none',
                            transition: 'background-color 0.2s',
                            backgroundColor: index === highlightedIndex ? 'var(--bs-secondary-bg)' : 'transparent',
                          }}
                          onClick={() => handleClientSelect(client)}
                          onMouseEnter={() => setHighlightedIndex(index)}
                        >
                          <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                            {highlightMatch(client.name, search)}
                          </div>
                          {client.phone_number && (
                            <div className="text-muted small">
                              <FaPhone className="me-1" /> {highlightMatch(client.phone_number, search)}
                            </div>
                          )}
                          {client.email && (
                            <div className="text-muted small">
                              <FaEnvelope className="me-1" /> {highlightMatch(client.email, search)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="p-3 text-center text-muted small">
                      Aucun client trouvé
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Icons on the right */}
            <Nav className="d-flex flex-row align-items-center gap-3">
              {/* Admin link */}
              {(isAdmin() || hasPermission('SEE_ADMIN')) && (
                <Nav.Item>
                  <NavLink to="/administration" className="nav-link p-0 btn-topbar-icon">
                    <FaCog />
                  </NavLink>
                </Nav.Item>
              )}

              {/* Notifications dropdown — mobile */}
              <Nav.Item className="position-relative">
                <Dropdown align="end">
                  <Dropdown.Toggle as={Button} variant="link" className="position-relative no-caret p-0 btn-topbar-icon">
                    <FaBell />
                    {notifCount > 0 && (
                      <Badge bg="danger" className="position-absolute top-0 start-100 translate-middle badge-counter">
                        {notifCount > 99 ? "99+" : notifCount}
                      </Badge>
                    )}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="shadow position-absolute" style={{ width: 320, right: 0, left: 'auto' }}>
                    <div className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center">
                      <span className="fw-semibold small">Mes tickets assignés</span>
                      {notifCount > 0 && <Badge bg="warning" text="dark">{notifCount}</Badge>}
                    </div>
                    {notifLoading ? (
                      <div className="p-3 text-center text-muted small">Chargement...</div>
                    ) : notifications.length === 0 ? (
                      <div className="p-3 text-center text-muted small">Aucun ticket assigné</div>
                    ) : (
                      <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                        {notifications.map(t => (
                          <Dropdown.Item key={t.id} as={Link} to={`/ticket/${t.id}`} className="d-flex align-items-start gap-2 py-2 px-3">
                            <div className="flex-grow-1 overflow-hidden">
                              <div className="small fw-semibold text-truncate">{t.title || `#${t.id}`}</div>
                              {t.client?.name && <div className="text-muted" style={{ fontSize: '0.72rem' }}>{t.client.name}</div>}
                            </div>
                            <Badge
                              bg={STATUS_BG[t.status] ?? 'secondary'}
                              text={t.status === 'in_progress' ? 'dark' : undefined}
                              className="flex-shrink-0 mt-1"
                            >
                              {STATUS_LABEL[t.status] ?? t.status}
                            </Badge>
                          </Dropdown.Item>
                        ))}
                      </div>
                    )}
                  </Dropdown.Menu>
                </Dropdown>
              </Nav.Item>

              {/* User profile */}
              <Nav.Item className="position-relative">
                <Dropdown align="end">
                  <Dropdown.Toggle as={Button} variant="link" className="d-flex align-items-center text-gray-600 small no-caret text-decoration-none p-0">
                    <div
                      style={{
                        width: '40px',
                        height: '40px',
                        borderRadius: '50%',
                        backgroundColor: '#4E73DF',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '18px',
                        overflow: 'hidden'
                      }}
                    >
                      {user?.data?.avatar ? (
                        <img 
                          src={user.data.avatar} 
                          alt="Avatar" 
                          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      ) : (
                        <FaUser />
                      )}
                    </div>
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="shadow position-absolute" style={{ right: 0, left: 'auto' }}>
                    <Dropdown.Item as={Link} to="/home">
                      <i className="fas fa-user fa-sm fa-fw mr-2 text-gray-400"></i> Mon activité
                    </Dropdown.Item>
                    <Dropdown.Item as={Link} to="/profil">
                      <i className="fas fa-cogs fa-sm fa-fw mr-2 text-gray-400"></i> Modifier mes informations
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={handleClick}>
                      <i className="fas fa-sign-out-alt fa-sm fa-fw mr-2 text-gray-400"></i> Déconnexion
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>
              </Nav.Item>
            </Nav>
          </div>
        </Navbar>

        <div
          id="alerteZone"
          style={{ marginTop: "calc(4.375rem + 10px)", position: "fixed", right: 10, zIndex: 99999 }}
        ></div>

        <a className="scroll-to-top rounded" href="#page-top">
          <i className="fas fa-angle-up"></i>
        </a>
      </>
    );
  }

  // Desktop version
  return (
    <>
      <Navbar expand="lg" className="shadow mb-4 navbar-fixed-top" style={{ zIndex: 1, height: '4.375rem' }}>
        <div className="d-flex align-items-center w-100">
          {/* Desktop client search */}
          <div className="d-none d-sm-flex align-items-center position-relative" style={{ marginLeft: "25px" }}>
            {/* Search input */}
            <Form.Control
              ref={searchInputRef}
              type="text"
              placeholder="Chercher un client..."
              className="form-control-user before-input"
              style={{ 
                width: "350px", 
                marginLeft: collapsed ? "0" : "220px",
                height: "40px", 
                borderTopRightRadius: 0, 
                borderBottomRightRadius: 0,
                transition: "margin-left 0.3s ease"
              }}
              value={search}
              onChange={handleSearchChange}
              onKeyDown={handleKeyDown}
              onFocus={() => search.trim().length > 0 && setShowDropdown(true)}
            />

            {/* Search button */}
            <Button
              variant="primary"
              className='' 
              style={{
                backgroundColor: "#4E73DF",
                border: "none",
                height: "40px",
                width: "40px",
                padding: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
                marginLeft: 0
              }}
              onClick={() => search.trim().length > 0 && setShowDropdown(true)}
            >
              <FaSearch />
            </Button>

            {/* Clients dropdown */}
            {showDropdown && (
              <div
                ref={dropdownRef}
                className="position-absolute bg-body shadow rounded"
                style={{
                  top: "110%",
                  left: "220px",
                  width: "350px",
                  maxHeight: "400px",
                  overflowY: "auto",
                  zIndex: 10,
                  color: "var(--bs-body-color)",
                }}
              >
                {isLoading ? (
                  <div className="p-3 text-center text-muted small">
                    Recherche en cours...
                  </div>
                ) : clients.length > 0 ? (
                  <div>
                    {clients.map((client, index) => (
                      <div
                        key={client.id}
                        className="p-2 cursor-pointer"
                        style={{
                          cursor: 'pointer',
                          borderBottom: index < clients.length - 1 ? '1px solid var(--bs-border-color)' : 'none',
                          transition: 'background-color 0.2s',
                          backgroundColor: index === highlightedIndex ? 'var(--bs-secondary-bg)' : 'transparent',
                        }}
                        onClick={() => handleClientSelect(client)}
                        onMouseEnter={() => setHighlightedIndex(index)}
                      >
                        <div className="font-weight-bold" style={{ fontSize: '0.9rem' }}>
                          {highlightMatch(client.name, search)}
                        </div>
                        {client.phone_number && (
                          <div className="text-muted small">
                            <FaPhone className="me-1" /> {highlightMatch(client.phone_number, search)}
                          </div>
                        )}
                        {client.email && (
                          <div className="text-muted small">
                            <FaEnvelope className="me-1" /> {highlightMatch(client.email, search)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-3 text-center text-muted small">
                    Aucun client trouvé
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex-grow-1"></div>

          <Nav className="ml-4 align-items-center">
            {/* Mobile client search */}
            <Nav.Item className="d-sm-none">
              <Dropdown align="end">
                <Dropdown.Toggle as={Button} variant="link">
                  <FaSearch />
                </Dropdown.Toggle>
                <Dropdown.Menu className="p-3 shadow">
                  <Form>
                    <Form.Control
                      type="text"
                      placeholder="Chercher un client..."
                      value={search}
                      onChange={handleSearchChange}
                      className="mb-2"
                    />
                    {isLoading ? (
                      <div className="text-center text-muted small">
                        Recherche...
                      </div>
                    ) : clients.length > 0 ? (
                      <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                        {clients.map((client) => (
                          <div
                            key={client.id}
                            className="p-2 cursor-pointer border-bottom"
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleClientSelect(client)}
                          >
                            <div className="font-weight-bold">{client.name}</div>
                            <div className="text-muted small">
                              {client.phone_number && <><FaPhone className="me-1" />{client.phone_number}</>}
                              {client.email && <><span className="mx-2">|</span><FaEnvelope className="me-1" />{client.email}</>}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : search.trim().length > 0 ? (
                      <div className="text-center text-muted small">
                        Aucun client trouvé
                      </div>
                    ) : null}
                  </Form>
                </Dropdown.Menu>
              </Dropdown>
            </Nav.Item>

            {/* Admin link */}
            {(isAdmin() || hasPermission('SEE_ADMIN')) && (
              <Nav.Item className="ml-3 d-none d-md-block">
                <NavLink to="/administration" className="nav-link btn-topbar-icon">
                  <FaCog />
                </NavLink>
              </Nav.Item>
            )}

            {/* Notifications dropdown \u2014 desktop */}
            <Nav.Item className="mx-3">
              <Dropdown align="end">
                <Dropdown.Toggle as={Button} variant="link" className="position-relative no-caret btn-topbar-icon">
                  <FaBell />
                  {notifCount > 0 && (
                    <Badge bg="danger" className="position-absolute top-0 start-100 translate-middle badge-counter">
                      {notifCount > 99 ? "99+" : notifCount}
                    </Badge>
                  )}
                </Dropdown.Toggle>
                <Dropdown.Menu className="shadow" style={{ minWidth: 320 }}>
                  <div className="px-3 py-2 border-bottom d-flex justify-content-between align-items-center">
                    <span className="fw-semibold small">Mes tickets assignés</span>
                    {notifCount > 0 && <Badge bg="warning" text="dark">{notifCount}</Badge>}
                  </div>
                  {notifLoading ? (
                    <div className="p-3 text-center text-muted small">Chargement...</div>
                  ) : notifications.length === 0 ? (
                    <div className="p-3 text-center text-muted small">Aucun ticket assigné</div>
                  ) : (
                    <div style={{ maxHeight: 360, overflowY: 'auto' }}>
                      {notifications.map(t => (
                        <Dropdown.Item key={t.id} as={Link} to={`/ticket/${t.id}`} className="d-flex align-items-start gap-2 py-2 px-3">
                          <div className="flex-grow-1 overflow-hidden">
                            <div className="small fw-semibold text-truncate">{t.title || `#${t.id}`}</div>
                            {t.client?.name && <div className="text-muted" style={{ fontSize: '0.72rem' }}>{t.client.name}</div>}
                          </div>
                          <Badge
                            bg={STATUS_BG[t.status] ?? 'secondary'}
                            text={t.status === 'in_progress' ? 'dark' : undefined}
                            className="flex-shrink-0 mt-1"
                          >
                            {STATUS_LABEL[t.status] ?? t.status}
                          </Badge>
                        </Dropdown.Item>
                      ))}
                    </div>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </Nav.Item>

            {/* Divider */}
            <div
              className="d-none d-sm-block"
              style={{
                width: '1px',
                height: '36px',
                backgroundColor: '#e3e6f0',
                margin: '0 12px',
                alignSelf: 'center'
              }}
            ></div>

            {/* User profile */}
            <Nav.Item>
              <Dropdown align="end">
                <Dropdown.Toggle as={Button} variant="link" className="d-flex align-items-center gap-3 text-gray-600 small no-caret text-decoration-none">
                  <span className="mr-2 d-none d-lg-inline">
                    {user?.data?.name && user?.data?.surname ? `${user.data.name} ${user.data.surname}` : ""}
                  </span>
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '50%',
                      backgroundColor: '#4E73DF',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'white',
                      fontSize: '18px',
                      overflow: 'hidden'
                    }}
                  >
                    {user?.data?.avatar ? (
                      <img 
                        src={user.data.avatar} 
                        alt="Avatar" 
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <FaUser />
                    )}
                  </div>
                </Dropdown.Toggle>
                <Dropdown.Menu className="shadow">
                  <Dropdown.Item as={Link} to="/home">
                    <i className="fas fa-user fa-sm fa-fw mr-2 text-gray-400"></i> Mon activité
                  </Dropdown.Item>
                  <Dropdown.Item as={Link} to="/profil">
                    <i className="fas fa-cogs fa-sm fa-fw mr-2 text-gray-400"></i> Modifier mes informations
                  </Dropdown.Item>
                  <Dropdown.Divider />
                  <Dropdown.Item onClick={handleClick}>
                    <i className="fas fa-sign-out-alt fa-sm fa-fw mr-2 text-gray-400"></i> Déconnexion
                  </Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown>
            </Nav.Item>
          </Nav>
        </div>
      </Navbar>

      <div
        id="alerteZone"
        style={{ marginTop: "calc(4.375rem + 10px)", position: "fixed", right: 10, zIndex: 99999 }}
      ></div>

      <a className="scroll-to-top rounded" href="#page-top">
        <i className="fas fa-angle-up"></i>
      </a>
    </>
  );
};

export default Topbar;